import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { cityLandingData, isValidCitySlug } from "@/mocks/cityLandingPages";
import { landListings } from "@/mocks/landListings";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";
import { usePageMeta } from "@/hooks/usePageMeta";

function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

const CITY_ALIASES: Record<string, string[]> = {
  bengaluru: ["Bengaluru", "Bangalore", "Bengaluru Rural", "North Bengaluru", "Bengaluru East"],
  mysuru: ["Mysuru", "Mysore"],
  mangaluru: ["Mangaluru", "Mangalore"],
  hubli: ["Hubli", "Hubballi"],
  belagavi: ["Belagavi", "Belgaum"],
  ballari: ["Ballari", "Bellary"],
  kalaburagi: ["Kalaburagi", "Gulbarga"],
  shivamogga: ["Shivamogga", "Shimoga"],
  udupi: ["Udupi"],
  tumakuru: ["Tumakuru", "Tumkur"],
  vijayapura: ["Vijayapura", "Bijapur"],
  davanagere: ["Davanagere", "Davangere"],
};

function matchCityToLocation(slug: string, location: string): boolean {
  const aliases = CITY_ALIASES[slug] || [slug];
  const loc = location.toLowerCase();
  return aliases.some((alias) => loc.includes(alias.toLowerCase()));
}

export default function CityLandingPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const { getContent } = usePageContent('karnataka-city-detail');
  const { settings } = useSettings();

  const isValid = !!citySlug && isValidCitySlug(citySlug);
  const city = isValid && citySlug ? cityLandingData[citySlug] : null;

  usePageMeta({
    title: city ? city.metaTitle : 'Karnataka Land & City Guides | CashMyProperty.ai',
    description: city ? city.metaDescription : 'Explore verified land listings and investment guides across Karnataka cities with CashMyProperty.ai.',
    keywords: city ? city.metaKeywords : undefined,
    canonicalUrl: city ? `https://cashmyproperty.ai/karnataka-cities/${city.slug}` : 'https://cashmyproperty.ai/karnataka-cities',
    type: 'article',
  });

  const cityListings = useMemo(() => {
    if (!citySlug || !isValid) return [];
    return landListings.filter((l) => matchCityToLocation(citySlug, l.location));
  }, [citySlug, isValid]);

  const schemaData = useMemo(() => {
    if (!city) return {};
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: city.metaTitle,
      description: city.metaDescription,
      about: {
        "@type": "Place",
        name: city.name,
        containedInPlace: {
          "@type": "State",
          name: "Karnataka",
        },
      },
      mainEntity: {
        "@type": "FAQPage",
        mainEntity: city.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    };
  }, [city]);

  useEffect(() => {
    if (!schemaData || Object.keys(schemaData).length === 0) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [schemaData]);

  // Redirect to home when the Karnataka Cities feature is disabled in dashboard settings
  if (settings.features.karnataka_cities_page === false) {
    return <Navigate to="/" replace />;
  }

  const aboutTitle = city ? stripHtml(getContent('city-about-heading')).replace('{city}', city.name) || `About ${city.name}` : '';
  const whyInvestTitle = city ? stripHtml(getContent('city-why-invest-heading')).replace('{city}', city.name) || `Why Invest in ${city.name} Land` : '';
  const keyAreasTitle = city ? stripHtml(getContent('city-key-areas-heading')).replace('{city}', city.name) || `Key Land Zones in ${city.name}` : '';
  const listingsTitle = city ? stripHtml(getContent('city-listings-heading')).replace('{city}', city.name) || `${city.name} Land Listings` : '';
  const faqTitle = city ? stripHtml(getContent('city-faq-heading')).replace('{city}', city.name) || `${city.name} Land — Frequently Asked` : '';
  const ctaTitle = city ? stripHtml(getContent('city-cta-heading')).replace('{city}', city.name) || `Own Land in ${city.name}?` : '';

  if (!isValid || !city) {
    return (
      <PageLayout>
        <section className="py-40 text-center bg-background-50">
          <div className="container mx-auto px-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-6">
              <i className="ri-map-pin-2-line text-3xl text-primary-500" />
            </div>
            <h1 className="text-3xl font-display font-bold text-foreground-950 mb-3">City Not Found</h1>
            <p className="text-foreground-600 mb-8">We couldn't find a city page for this location. Browse all Karnataka cities below.</p>
            <Link
              to="/karnataka-cities"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              View All Cities
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-950 flex items-center">
        <img
          src={city.heroImage}
          alt={`${city.name} Land Opportunities`}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-950" />
        <div className="relative container mx-auto px-4 lg:px-8 py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/40 bg-primary-500/10 mb-6">
              <i className="ri-map-pin-2-line text-primary-400 text-sm" />
              <span className="text-primary-400 text-xs font-semibold uppercase tracking-widest">
                {city.region} · {city.heroBadge}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-5">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">
                {city.headline}{" "}
              </span>
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">
                {city.highlight}
              </span>
            </h1>
            <p className="text-lg text-white/80 max-w-xl mb-8 leading-relaxed [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              {city.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/submit-property"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-map-pin-add-line" />
                List Your {city.name} Land Free
              </Link>
              <a
                href={`/listings?city=${encodeURIComponent(city.name)}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `/listings?city=${encodeURIComponent(city.name)}`;
                }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-primary-500/40 text-primary-400 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-line" />
                Browse {city.name} Listings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-background-950 border-b border-primary-800/20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {city.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/15 mx-auto mb-2">
                  <i className={`${stat.icon} text-primary-400 text-sm`} />
                </div>
                <p className="text-xl md:text-2xl font-display font-bold text-white mb-0.5">{stat.value}</p>
                <p className="text-xs text-white/50 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About the City */}
      <section className="py-20 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-6">
              {aboutTitle}
            </h2>
            {city.aboutParagraphs.map((para, i) => (
              <p key={i} className="text-foreground-600 leading-relaxed mb-5">
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Why Invest */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Investment Case
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950">
              {whyInvestTitle}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {city.whyInvestItems.map((item) => (
              <div
                key={item.title}
                className="flex gap-5 p-6 rounded-2xl card-dark group hover:border-primary-500/40 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-500/20 shrink-0 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${item.icon} text-xl text-primary-500`} />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-base text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Land Zones */}
      <section className="py-20 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Land Zones
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950">
              {keyAreasTitle}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {city.keyAreas.map((area) => (
              <div
                key={area.name}
                className="p-6 rounded-2xl card-dark group hover:border-primary-500/40 transition-all duration-300"
              >
                <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-600 mb-3">
                  {area.type}
                </span>
                <h3 className="font-display font-semibold text-lg text-foreground-950 mb-1">{area.name}</h3>
                <p className="text-sm font-semibold text-primary-600 mb-3">{area.avgRent}/month</p>
                <p className="text-sm text-foreground-600 leading-relaxed">{area.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Listings */}
      {cityListings.length > 0 && (
        <section className="py-20 bg-background-100">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
              <div>
                <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
                  Available Now
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-2">
                  {listingsTitle}
                </h2>
                <p className="text-foreground-600">
                  {cityListings.length} verified {cityListings.length === 1 ? "listing" : "listings"} currently available in {city.name}
                </p>
              </div>
              <a
                href={`/listings?city=${encodeURIComponent(city.name)}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `/listings?city=${encodeURIComponent(city.name)}`;
                }}
                className="inline-flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              >
                View All {city.name} Listings
                <i className="ri-arrow-right-line" />
              </a>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {cityListings.map((listing) => (
                <div
                  key={listing.id}
                  className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300 flex flex-col"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={listing.image}
                      alt={listing.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${listing.statusColor}`}>
                        {listing.status}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                        {listing.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                      <i className="ri-map-pin-line text-primary-500" />
                      {listing.location}
                    </div>
                    <h3 className="font-display font-semibold text-base text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {listing.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3 flex-1 line-clamp-2">
                      {listing.description}
                    </p>
                    <div className="grid grid-cols-2 gap-2 mt-auto pt-3 border-t border-primary-900/20">
                      <div className="p-2 rounded-lg bg-background-200/50 border border-primary-800/20">
                        <div className="text-xs text-foreground-500">Size</div>
                        <div className="text-sm font-semibold text-foreground-950">{listing.size}</div>
                      </div>
                      <div className="p-2 rounded-lg bg-background-200/50 border border-primary-800/20">
                        <div className="text-xs text-foreground-500">Expected Rent</div>
                        <div className="text-sm font-semibold text-primary-600">{listing.expectedRent}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use Case Tags */}
      <section className="py-12 bg-background-50 border-t border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <p className="text-sm text-foreground-500 mb-4 uppercase tracking-wider">AI-Modelled Use Cases for {city.name}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {city.useCaseTags.map((tag) => (
              <span
                key={tag}
                className="px-4 py-2 rounded-full text-sm font-medium bg-secondary-100 text-secondary-800 border border-secondary-200"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-14">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Common Questions
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950">
              {faqTitle}
            </h2>
          </div>
          <div className="space-y-4">
            {city.faqs.map((faq, i) => (
              <details key={i} className="group rounded-2xl card-dark overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer hover:bg-primary-500/5 transition-colors list-none">
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500/10 shrink-0 group-open:rotate-45 transition-transform duration-300">
                    <i className="ri-add-line text-primary-500" />
                  </div>
                </summary>
                <div className="px-6 pb-5">
                  <p className="text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Local SEO paragraph */}
      <section className="py-12 bg-background-50 border-t border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <p className="text-sm text-foreground-500 leading-relaxed">
            {city.localSeoParagraph}
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-background-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
        <div className="relative container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            {ctaTitle}
          </h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            Get an AI feasibility report for your {city.name} plot within 48 hours. We'll rank the top 4 monetization paths — Joint Development, Built-to-Suit Leasing, Franchise Setup, or Land-as-Equity — with projected rental income for each.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-property"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-map-pin-add-line" />
              List Your {city.name} Land Free
            </Link>
            <Link
              to="/karnataka-cities"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-500/40 text-primary-400 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-map-pin-2-line" />
              Explore All Cities
            </Link>
          </div>
        </div>
      </section>

      {/* Breadcrumb nav */}
      <section className="py-4 bg-background-50 border-t border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/karnataka-cities" className="hover:text-primary-500 transition-colors">Karnataka Cities</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">{city.name} ({city.nameAlt})</span>
          </nav>
        </div>
      </section>
    </PageLayout>
  );
}